import { FunctionComponent, useEffect, useState } from 'react';
import TextInputField from '../../Components/TextInput/TextInputField';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import {
  addSpaceTypes,
  clearAddSpaceTypesState,
} from '../../Redux/Slicers/SpaceTypes/AddSpaceTypesSlicer';
import { useAppDispatch, useAppSelector } from '../../Redux/Store/Hooks';
import {
  clearUpdateSpaceTypesState,
  updateSpaceTypes,
} from '../../Redux/Slicers/SpaceTypes/UpdateSpaceTypesSlicer';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Select from '../../Components/Select/Select';
import { getSpaceTypes } from '../../Redux/Slicers/SpaceTypes/SpaceTypesSlicer';
import { getDesignation } from '../../Redux/Slicers/Designation/DesignationSlicer';
import { ISpaceTypesList, WorkingHoursMap } from './ISpaceTypes';

interface SpaceTypesFormProps {
  SpaceTypesForm: (data: FormikProps<any>) => void;
}
const SpaceTypesForm: FunctionComponent<SpaceTypesFormProps> = (props) => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const pathnameParts = location.pathname.split('/');
  const id = pathnameParts[pathnameParts?.length - 1];
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [setdisable, setDisable] = useState(false);
  const [showWorkingTime, setShowWorkingTime] = useState(false);

  const { updateableDate } = useAppSelector((state) => state?.UpdateSpaceTypes);
  const { data: designation } = useAppSelector((state) => state?.Designation);

  useEffect(() => {
    if (id) {
      dispatch(getSpaceTypes(id));
      dispatch(getDesignation(id));
    }
  }, [dispatch, id]);

  const defaultWorkingHours: WorkingHoursMap = {
    sun: { open: false, start: '', end: '' },
    mon: { open: false, start: '', end: '' },
    tues: { open: false, start: '', end: '' },
    wed: { open: false, start: '', end: '' },
    thurs: { open: false, start: '', end: '' },
    fri: { open: false, start: '', end: '' },
    sat: { open: false, start: '', end: '' },
  };

  const formik = useFormik<ISpaceTypesList>({
    initialValues: {
      id: null,
      _id: '',
      name: '',
      time_limit: 0,
      location_id: id,
      restriction: '',
      user_id: [],
      id1: '',
      restriction_data: [],
      value: null,
      working_hours: defaultWorkingHours,
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('Please enter Name'),
      time_limit: Yup.number()
        .min(0, 'Time limit must be 0 or a greater than 0')
        .required('Please enter a valid time limit'),
    }),
    onSubmit: (value) => {
      if (updateableDate && Object.keys(updateableDate)?.length !== 0) {
        dispatch(
          updateSpaceTypes({
            ...value,
            location_id: id,
            id: updateableDate._id,
          })
        );
      } else {
        const dd = { ...value, location_id: id };
        dispatch(addSpaceTypes(dd));
      }
    },
  });

  useEffect(() => {
    props.SpaceTypesForm(formik);
    if (updateableDate) {
      formik.setFieldValue('name', updateableDate.name);
      formik.setFieldValue('time_limit', updateableDate.time_limit);
      const restrictionOptions = [
        { label: 'Only allow certain user designations', value: 'allow' },
        { label: 'Disallow certain user designations', value: 'disallow' },
        { label: 'None', value: '' },
      ];
      formik.setFieldValue(
        'restriction',
        restrictionOptions.find((opt) => opt.value === updateableDate.restriction) ?? { label: 'None', value: '' }
      );

      const userIds = updateableDate?.restriction_data?.map((user: any) => ({
        label: user?.name,
        value: user?._id,
      }));
      formik.setFieldValue(
        'restriction_data',
        userIds?.map((user: any) => user?.value)
      );

      setSelectedUsers(userIds ? userIds : []);

      if (updateableDate.working_hours) {
        formik.setFieldValue(
          'working_hours',
          updateableDate.working_hours as WorkingHoursMap
        );
      }
    }

    const hasWorkingTime = Boolean(
      updateableDate?.working_hours &&
        Object.values(updateableDate.working_hours).some(
          (time: any) => Boolean(time?.open || time?.start || time?.end)
        )
    );
    setShowWorkingTime(hasWorkingTime);

    return () => {
      dispatch(clearAddSpaceTypesState());
      dispatch(clearUpdateSpaceTypesState());
      formik.resetForm();
    };
  }, [updateableDate, dispatch]);

  const userOptions =
    designation?.response?.data
      ?.map((user: any) => ({
        label: user?.name,
        value: user?._id,
      }))
      ?.filter(
        (userOption) =>
          !selectedUsers?.some(
            (selected) => selected?.value === userOption?.value
          )
      ) || [];

  const renderWorkingHoursFields = (day: keyof WorkingHoursMap) => {
    const workingHours = formik.values.working_hours || defaultWorkingHours;
    const dayKey = day as string;
    const dayHours = workingHours[day];

    return (
      <>
        <div style={{ display: 'flex', columnGap: '10px' }}>
          <Select
            sx={{ width: '50%' }}
            id={`working_hours.${dayKey}.open`}
            name={`working_hours.${dayKey}.open`}
            label={`${dayKey.charAt(0).toUpperCase() + dayKey.slice(1)} Status`}
            value={dayHours.open ? 'Reserved' : 'Regular'}
            options={['Reserved', 'Regular']}
            onChange={(_, value: any) => {
              formik.setFieldValue(
                `working_hours.${dayKey}.open`,
                value === 'Reserved'
              );
            }}
            onBlur={formik.handleBlur}
            placeholder="-select-"
          />
          {dayHours.open && (
            <>
              <TextInputField
                sx={{ width: '25%' }}
                id={`working_hours.${dayKey}.start`}
                name={`working_hours.${dayKey}.start`}
                label={`${dayKey.charAt(0).toUpperCase() + dayKey.slice(1)} Start Time`}
                value={dayHours.start}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="00:00"
              />
              <TextInputField
                sx={{ width: '25%' }}
                id={`working_hours.${dayKey}.end`}
                name={`working_hours.${dayKey}.end`}
                label={`${dayKey.charAt(0).toUpperCase() + dayKey.slice(1)} End Time`}
                value={dayHours.end}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="00:00"
              />
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <form
      className="master_form"
      onSubmit={formik.handleSubmit}
      onReset={formik.handleReset}
      autoComplete="off">
      <TextInputField
        id="name"
        name="name"
        label="Name"
        value={formik.values.name}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        helperText={formik.touched.name && formik.errors.name}
        error={formik.touched.name && Boolean(formik.errors.name)}
      />

      <h6>Enter 0 for this space type to have no time limit</h6>
      <TextInputField
        type="number"
        id="time_limit"
        name="time_limit"
        label="Time limit in minutes"
        value={formik.values.time_limit}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        helperText={formik.touched.time_limit && formik.errors.time_limit}
        error={formik.touched.time_limit && Boolean(formik.errors.time_limit)}
      />

      <Select
        id="restriction"
        name="restriction"
        label="Restriction Type"
        value={formik.values.restriction}
        options={[
          { label: 'Only allow certain user designations', value: 'allow' },
          { label: 'Disallow certain user designations', value: 'disallow' },
          { label: 'None', value: '' },
        ]}
        onChange={(_, value: any) => {
          if (!value.value) {
            formik.setFieldValue('restriction_data', []);
            setSelectedUsers([]);
            setDisable(true);
          } else {
            setDisable(false);
          }
          formik.setFieldValue('restriction', value);
        }}
        onBlur={formik.handleBlur}
        helperText={
          formik.touched.restriction &&
          typeof formik.errors.restriction === 'string'
            ? formik.errors.restriction
            : ''
        }
        error={formik.touched.restriction && Boolean(formik.errors.restriction)}
        placeholder="-select-"
      />

      <Autocomplete
        multiple
        disabled={
          !formik?.values?.restriction?.value ||
          setdisable
            ? true
            : false
        }
        options={userOptions}
        getOptionLabel={(option) => option?.label}
        value={selectedUsers}
        onChange={(event, newValue) => {
          const selectedIds = newValue?.map((option) => option?.value);
          formik?.setFieldValue('restriction_data', selectedIds);
          setSelectedUsers(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add Designations"
            placeholder="Select Designations"
          />
        )}
      />

      <div style={{ width: '100%', marginTop: 16 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showWorkingTime}
              onChange={(event) => setShowWorkingTime(event.target.checked)}
            />
          }
          label="Working Time"
        />
        {showWorkingTime &&
          Object.keys(formik.values.working_hours || defaultWorkingHours).map(
            (day) => (
              <div style={{ width: '100%', marginTop: 8 }} key={day}>
                {renderWorkingHoursFields(day as keyof WorkingHoursMap)}
              </div>
            )
          )}
      </div>

      <button
        type="submit"
        style={{ display: 'none' }}
      />
    </form>
  );
};

export default SpaceTypesForm;
